import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Modal } from 'antd';
import styled from 'styled-components';
import { CloseOutlined } from '@ant-design/icons';

// Styled components
const StyledModal = styled(Modal)`
	.ant-modal-content {
		padding: 0;
		border-radius: 8px;
		overflow: hidden;
	}

	.ant-modal-body {
		padding: 0;
		height: 80vh;
		display: flex;
		align-items: center;
		justify-content: center;
		background-color: #f5f5f5;
	}

	.ant-modal-header {
		padding: 16px 24px;
		border-bottom: 1px solid #f0f0f0;
		background-color: #fff;
	}

	.ant-modal-title {
		font-weight: 600;
		font-size: 16px;
		color: #262626;
	}

	.ant-modal-close {
		top: 16px;
		right: 16px;
	}
`;

const DocumentContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	background-color: #f5f5f5;
`;

const DocumentFrame = styled.iframe`
	width: 100%;
	height: 100%;
	border: none;
`;

const ImageContainer = styled.div`
	width: 100%;
	height: 100%;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: 24px;
	overflow: auto;
`;

const DocumentImage = styled.img`
	max-width: 100%;
	max-height: 100%;
	object-fit: contain;
	border-radius: 4px;
	box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
`;

const ErrorMessage = styled.div`
	padding: 48px;
	text-align: center;
	color: #8c8c8c;
	font-size: 14px;
`;

// Constants
const DEFAULT_DOCUMENT_TITLE = 'Document Viewer';

/**
 * DocumentViewerModal - Common modal component for viewing documents
 * Supports PDF, images, and other document types
 *
 * @param {boolean} visible - Controls modal visibility
 * @param {function} onClose - Callback when modal is closed
 * @param {string} documentUrl - URL of the document to display
 * @param {string} documentName - Name/title of the document
 * @param {string} documentType - Type of document (pdf, image, etc.)
 */
function DocumentViewerModal({ visible, onClose, documentUrl, documentName, documentType }) {
	const [imageLoadError, setImageLoadError] = useState(false);

	/**
	 * Determine the document type based on URL or explicit type
	 */
	const getDocumentType = () => {
		if (documentType) return documentType.toLowerCase();

		if (!documentUrl) return 'unknown';

		const url = documentUrl.toLowerCase();
		if (url.includes('.pdf') || url.includes('pdf')) return 'pdf';
		if (
			url.includes('.jpg') ||
			url.includes('.jpeg') ||
			url.includes('.png') ||
			url.includes('.gif') ||
			url.includes('.webp')
		) {
			return 'image';
		}
		return 'unknown';
	};

	// Reset error state when document URL changes
	React.useEffect(() => {
		setImageLoadError(false);
	}, [documentUrl]);

	/**
	 * Render document content based on type
	 */
	const renderDocumentContent = () => {
		if (!documentUrl) {
			return <ErrorMessage>No document URL provided</ErrorMessage>;
		}

		const type = getDocumentType();

		switch (type) {
			case 'pdf':
				return (
					<DocumentContainer>
						<DocumentFrame
							src={`${documentUrl}#toolbar=1&navpanes=0&scrollbar=1`}
							title={documentName || DEFAULT_DOCUMENT_TITLE}
							onError={(e) => {
								console.error('PDF iframe load error:', e);
							}}
						/>
					</DocumentContainer>
				);

			case 'image':
				if (imageLoadError) {
					return (
						<ImageContainer>
							<ErrorMessage>
								Failed to load image. The file may be corrupted or the URL has
								expired.
							</ErrorMessage>
						</ImageContainer>
					);
				}
				return (
					<ImageContainer>
						<DocumentImage
							src={documentUrl}
							alt={documentName || 'Document'}
							onError={(e) => {
								console.error('Image load error:', e);
								setImageLoadError(true);
							}}
						/>
					</ImageContainer>
				);

			default:
				// For other document types, try iframe
				return (
					<DocumentContainer>
						<DocumentFrame
							src={documentUrl}
							title={documentName || DEFAULT_DOCUMENT_TITLE}
							onError={(e) => {
								console.error('Document iframe load error:', e);
							}}
						/>
					</DocumentContainer>
				);
		}
	};

	return (
		<StyledModal
			title={documentName || DEFAULT_DOCUMENT_TITLE}
			open={visible}
			onCancel={onClose}
			footer={null}
			width="90vw"
			centered
			destroyOnClose
			closeIcon={<CloseOutlined />}
		>
			{renderDocumentContent()}
		</StyledModal>
	);
}

DocumentViewerModal.propTypes = {
	visible: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
	documentUrl: PropTypes.string,
	documentName: PropTypes.string,
	documentType: PropTypes.string,
};

DocumentViewerModal.defaultProps = {
	documentUrl: '',
	documentName: 'Document',
	documentType: null,
};

export default DocumentViewerModal;
